import React, { useEffect, useState } from "react";
import { Dropdown, DropdownItem, DropdownMenu, DropdownTrigger } from "@nextui-org/react";
import { format, parseISO } from "date-fns";
import { HiOutlineDotsHorizontal } from "react-icons/hi";
import { BsChatLeftText, BsPencil } from "react-icons/bs";
import { IoMdCheckmark } from "react-icons/io";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import myAxios from "../../../utils/axiosWrapper";
import getConfig from "../../../conf/config";
import TimecodeBadge from "../CallModal/TimecodeBadge";
import { formatTimecode } from "../../../components/MAudioPlayer/timecode";

const ACTIVITY_ICONS = {
  overview_edited: BsPencil,
};

const CommentDisplay = ({ comment, fetchComments, onSeek }) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [commentInput, setCommentInput] = useState(comment.content);
  const [actionError, setActionError] = useState("");

  // Seconds into the call audio the comment refers to (comments posted while
  // the call was playing). Older comments have none.
  const audioTimestamp = Number.isFinite(comment?.audio_timestamp) ? comment.audio_timestamp : null;

  // Entries logged by the app rather than typed by a user. Older comments
  // predate the field and have no `kind`, so they read as messages.
  const isLoggedEvent = !!comment?.kind && comment.kind !== "message";

  const onDeleteComment = async () => {
    setActionError("");
    try {
      await myAxios.delete(`${getConfig().API_URL}/audio/api/v1/comments/${comment.id}`);
    } catch (error) {
      console.error("Error deleting comment:", error);
      setActionError(t("Impossible de supprimer le message"));
      return;
    }

    fetchComments();
  };

  const onUpdateComment = async () => {
    if (commentInput?.length <= 0) {
      return;
    }

    setActionError("");
    try {
      await myAxios.put(`${getConfig().API_URL}/audio/api/v1/comments/update/${comment.id}`, {
        content: commentInput,
      });
    } catch (error) {
      console.error("Error updating comment:", error);
      setActionError(t("Impossible de modifier le message"));
      return;
    }

    setIsEditing(false);
    fetchComments();
  };

  const onDetectEnterComment = (e) => {
    if (e.key === "Enter") {
      onUpdateComment();
    }
  };

  const { user } = useSelector((state) => state.user);

  const isMyComment = comment?.creator_id === user?.uid;

  const onDropdownAction = (comment) => (action) => {
    const isEditAction = action === "edit";
    const isDeleteAction = action === "delete";

    if (isDeleteAction) {
      onDeleteComment();
    }

    if (isEditAction) {
      setIsEditing(true);
    }
  };

  if (isLoggedEvent) {
    const EventIcon = ACTIVITY_ICONS[comment.kind] || BsChatLeftText;

    return (
      <div className="flex items-start gap-2 text-xs text-[#404040]/[.7] mb-3">
        <EventIcon
          size={12}
          className="mt-[3px] shrink-0"
        />
        <div>
          <span className="font-semibold">{comment?.creator_name}</span> {t(comment.content)}
          <div className="text-[#404040]/[.5]">
            {format(parseISO(comment?.created_at), "dd/MM/yyyy HH:mm")}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="text-xs mb-3">
      <div className="flex items-center mb-1">
        <div className="font-semibold">{comment?.creator_name}</div>
        <div className="w-[4px] h-[4px] rounded-full bg-[#404040]/[.6] mx-2" />
        <div className="text-[#404040]/[.6]">
          {format(parseISO(comment?.created_at), "dd/MM/yyyy HH:mm")}
        </div>
        {audioTimestamp !== null &&
          (onSeek ? (
            <TimecodeBadge
              seconds={audioTimestamp}
              on_seek={onSeek}
              className="ml-2"
            />
          ) : (
            <span className="ml-2 text-primary">{formatTimecode(audioTimestamp)}</span>
          ))}
        {isMyComment && !isEditing && (
          <div className="ml-auto">
            <Dropdown>
              <DropdownTrigger>
                <div className="cursor-pointer">
                  <HiOutlineDotsHorizontal
                    size={18}
                    className="text-[#404040]/[.6]"
                  />
                </div>
              </DropdownTrigger>
              <DropdownMenu
                variant="faded"
                aria-label="Comment Actions"
                onAction={onDropdownAction(comment)}
              >
                <DropdownItem key="edit">{t("Modifier")}</DropdownItem>
                <DropdownItem
                  key="delete"
                  className="text-danger"
                  color="danger"
                >
                  {t("Supprimer")}
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        )}
      </div>
      {!isEditing && (
        <div className="flex items-center rounded-md border-1 p-2 border-[#E5E5E5] border-solid">
          {comment.content}
        </div>
      )}
      {isEditing && (
        <div className="flex items-center rounded-md border-2 border-[#E5E5E5] border-solid bg-white text-sm mt-4">
          <div className="flex h-[36px] items-center justify-center mr-2 px-3 bg-[#F5F5F5] border-r-2 border-r-[#E5E5E5] border-r-solid">
            <BsChatLeftText color="#737373" />
          </div>
          <input
            className="outline-none	border-0 h-[36px] w-full"
            placeholder={t("Message")}
            value={commentInput}
            onChange={(e) => setCommentInput(e.target.value)}
            onKeyDown={onDetectEnterComment}
          />
          <div
            className="cursor-pointer flex h-[32px] w-[32px] items-center bg-[#4318FF] justify-center ml-2 mr-[2px] rounded-md p-[4px]"
            onClick={() => onUpdateComment()}
            onKeyDown={() => onUpdateComment()}
          >
            <IoMdCheckmark color="#FFFFFF" />
          </div>
        </div>
      )}
      {actionError && <div className="text-danger mt-1">{actionError}</div>}
    </div>
  );
};

export default CommentDisplay;
